// ─── Public Status Page — what happened on one day ───────────────────
//
// EXTRACTED from HistoryGrid's tooltip, not copied. The tooltip and the day page
// answer the same question — "which incidents were in effect on this date?" — and
// two implementations of that would drift the moment one of them learned about a
// new field. This subsystem has already shipped four bugs from exactly that
// (the subscribe wire contract, the reach count, the audience filter, the
// incident create shape), so the rule here is one definition, two callers.
//
// Everything comes from the summary payload the page ALREADY holds. No new API
// call and no new snapshot file: the snapshot is regenerated on every status
// change, so per-day files would mean rewriting hundreds of them during an
// incident — for data that is already on the wire. It also means a day page
// opened from an email still works off the static snapshot while the API is
// down, which is the same promise the component detail page makes.

import type { PublicStatusIncident } from '@pcp/shared';

/** Local-midnight bounds of `day`, in the READER's timezone — the same bucketing
 *  the grid uses, so a cell and its day page can never disagree about which
 *  incidents belong to it. */
export function dayBounds(day: Date): { start: number; end: number } {
  const start = new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime();
  const end = new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1).getTime();
  return { start, end };
}

const toTime = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
};

/**
 * The incidents in effect on `day`, optionally narrowed to a set of components.
 *
 * `componentIds` is a SET rather than a single id so a day page opened from a
 * parent's bar covers that parent's whole subtree — the bar itself is rolled up,
 * so its day page has to be too, or clicking a red cell on a collapsed root
 * would show nothing.  `null` means "the whole page", which is what the overall
 * bar passes.
 *
 * A maintenance window is announced ahead of time, so its SCHEDULED start is the
 * real beginning of the affected period; a still-open incident has no end yet and
 * therefore runs up to now.
 */
export function incidentsOnDay(
  incidents: readonly PublicStatusIncident[],
  componentIds: ReadonlySet<string> | null,
  day: Date,
): PublicStatusIncident[] {
  if (incidents.length === 0) return [];
  const { start, end } = dayBounds(day);
  const now = Date.now();
  const out: PublicStatusIncident[] = [];
  for (const incident of incidents) {
    if (componentIds && !(incident.components ?? []).some((c) => componentIds.has(c.id))) continue;
    const from = toTime(incident.scheduledStartAt) ?? toTime(incident.occurredAt);
    if (from === null || from >= end) continue;
    const to = toTime(incident.resolvedAt) ?? toTime(incident.scheduledEndAt) ?? now;
    if (to < start) continue;
    out.push(incident);
  }
  return out;
}

/** Every component id in a subtree, for scoping a day page to a rolled-up bar. */
export function subtreeIds(node: { id: string; children?: { id: string }[] }): Set<string> {
  const ids = new Set<string>();
  const walk = (n: { id: string; children?: { id: string }[] }): void => {
    ids.add(n.id);
    for (const child of n.children ?? []) walk(child as { id: string; children?: { id: string }[] });
  };
  walk(node);
  return ids;
}

/** `YYYY-MM-DD` → a LOCAL Date at midnight, or null if it is not a real day.
 *  `new Date('2026-02-31')` silently rolls to 3 March, which would render a page
 *  about a date that does not exist — so the parse is checked, not trusted. */
export function parseDayParam(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value ?? '').trim());
  if (!m) return null;
  const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null;
  return d;
}

/** A LOCAL Date → the `YYYY-MM-DD` the route carries. Built from local parts, not
 *  toISOString(), which converts to UTC first and would name the wrong day for
 *  anyone east of Greenwich in the evening. */
export function dayParam(day: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
}
